import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { type PbshoppingMenuItem } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import { usePostHog } from '@/application/use-posthog'

interface StoreMenuProps {
    categories?: Array<PbshoppingMenuItem>
    onChangeCategory: () => void
}

export const StoreMenu = ({ categories, onChangeCategory }: StoreMenuProps) => {
    const { t } = useTranslation()

    const { captureEventPosthog } = usePostHog()

    const [searchParams] = useSearchParams()

    const group = useAppSelector(
        (store) => store.settingsApi.settings.response.ecommerce_group
    )

    const theme = useAppSelector(
        (store) => store.settingsApi.settings.response.branding
    )

    const ecommerceId = useAppSelector(
        (store) => store.settingsApi.settings.response.ecommerce_id
    )

    const createQueryString = (category: PbshoppingMenuItem, index: number) => {
        const params = new URLSearchParams(searchParams.toString())

        params.set('resource_id', category.resource_id ?? '')
        params.set('resource_type', category.resource_type ?? '')
        params.set('category', index.toString())

        return params.toString()
    }

    const handleCategoryChange = (
        category: PbshoppingMenuItem,
        index: number
    ) => {
        captureEventPosthog('FC - Change category', {
            categoryName: category.title,
            categoryIndex: index,
            ecommerceId
        })

        onChangeCategory()
    }

    const isMisako = group === 'misako'
    const isHoff = group === 'hoff'

    if (isHoff) {
        return (
            <nav>
                <ul className="flex flex-wrap gap-4">
                    {categories?.map((category, index) => (
                        <li
                            key={category.resource_id ?? index}
                            data-selected={
                                searchParams.get('category')
                                    ? index.toString() ===
                                      searchParams.get('category')
                                    : index === 0
                            }
                            className="rounded-lg border-neutral-200 text-sm uppercase text-neutral-800 data-[selected=true]:bg-neutral-900 data-[selected=true]:text-white lg:text-base"
                        >
                            <Link
                                onClick={() =>
                                    handleCategoryChange(category, index)
                                }
                                to={{
                                    search:
                                        '?' + createQueryString(category, index)
                                }}
                                replace
                                className="inline-block w-full px-3.5 py-2 font-haas"
                            >
                                {!category?.title
                                    ? t('full_catalog.all_products')
                                    : category?.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
        )
    }

    if (isMisako) {
        return (
            <nav>
                <ul className="flex flex-wrap gap-8">
                    {categories?.map((category, index) => (
                        <li
                            key={category.resource_id ?? index}
                            data-selected={
                                searchParams.get('category')
                                    ? index.toString() ===
                                      searchParams.get('category')
                                    : index === 0
                            }
                            className="rounded-md border-neutral-200 text-base text-neutral-900 underline-offset-8 hover:text-neutral-900 data-[selected=true]:underline"
                        >
                            <Link
                                onClick={() =>
                                    handleCategoryChange(category, index)
                                }
                                to={{
                                    search:
                                        '?' + createQueryString(category, index)
                                }}
                                replace
                                className="font-inter text-sm font-light tracking-wider"
                            >
                                {!category?.title
                                    ? t('full_catalog.all_products')
                                    : category?.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
        )
    }

    return (
        <aside className="flex size-full w-60 max-w-60 flex-col gap-6 border-r border-solid border-neutral-900/10 bg-white pb-5 pt-6">
            {theme?.logo_url ? (
                <div className="max-h-96 px-4">
                    <div className="max-w-44 pl-1">
                        <img
                            src={theme?.logo_url}
                            alt={theme?.title}
                            width={176}
                            height={40}
                            className="h-full max-h-14 w-auto object-contain"
                        />
                    </div>
                </div>
            ) : null}
            <nav className="overflow-y-auto px-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5">
                <ul className="space-y-1">
                    {categories?.map((category, index) => (
                        <li
                            key={category.resource_id ?? index}
                            data-selected={
                                searchParams.get('category')
                                    ? index.toString() ===
                                      searchParams.get('category')
                                    : index === 0
                            }
                            className="rounded-md border-neutral-200 text-base font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 data-[selected=true]:bg-neutral-100 data-[selected=true]:text-neutral-900"
                        >
                            <Link
                                onClick={() =>
                                    handleCategoryChange(category, index)
                                }
                                to={{
                                    search:
                                        '?' + createQueryString(category, index)
                                }}
                                replace
                                className="inline-block w-full px-3.5 py-2"
                            >
                                {!category?.title
                                    ? t('full_catalog.all_products')
                                    : category?.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
        </aside>
    )
}
